import { DeclarationV3Status } from '@/@types/Declarations.type';
import type { DeclarationsSummary, SummaryAnnual, SummaryCell, SummaryMonth } from './types';

const SELLABLE_STATUSES: string[] = [DeclarationV3Status.PENDING, DeclarationV3Status.READY_FOR_APPROVAL];

const IN_PROGRESS_STATUSES: string[] = [
	DeclarationV3Status.ACQUIRED,
	DeclarationV3Status.IN_PROGRESS,
	DeclarationV3Status.READY_FOR_APPROVAL,
	DeclarationV3Status.IN_REVIEW,
];

const CLOSED_ANNUAL_STATUSES: string[] = [
	DeclarationV3Status.PRESENTED,
	DeclarationV3Status.FUTURE,
	DeclarationV3Status.NOT_STARTED_ACTIVITY,
];

const numberOr = (value: unknown, fallback = 0) => (typeof value === 'number' ? value : fallback);

const cellOf = (declaration: any): SummaryCell => {
	const status = typeof declaration?.status === 'string' ? declaration.status : '';
	return {
		regime: declaration?.type,
		income: numberOr(declaration?.income),
		hadRegime: typeof declaration?.hasRegimeThisMonth === 'boolean' ? declaration.hasRegimeThisMonth : undefined,
		daysLate: numberOr(declaration?.daysLate),
		status,
		pending: status === DeclarationV3Status.PENDING,
		sellable: SELLABLE_STATUSES.includes(status) && !declaration?.isAcquired && !declaration?.isFuture,
		inProgress: IN_PROGRESS_STATUSES.includes(status),
	};
};

const incomeByRegime = (rows: readonly any[]) => {
	const totals: Record<string, number> = {};
	for (const row of rows) {
		const income = row?.income;
		if (!income || typeof income !== 'object') continue;
		for (const [regime, value] of Object.entries(income)) {
			if (regime !== 'total' && typeof value === 'number' && value > 0) totals[regime] = (totals[regime] ?? 0) + value;
		}
	}
	return totals;
};

const hasAnnualObligation = (annual: any, monthlyRows: readonly any[]) => {
	if (numberOr(annual?.income) <= 0) return false;
	const regimes = Object.keys(incomeByRegime(monthlyRows.filter((row) => row?.year === annual?.year)));
	if (regimes.length === 0) return false;
	return !(regimes.length === 1 && regimes[0] === 'resico');
};

const annualOf = (annual: any, monthlyRows: readonly any[]): SummaryAnnual => {
	const status = typeof annual?.status === 'string' ? annual.status : '';
	const purchased = Boolean(annual?.isAcquired);
	return {
		year: annual?.year,
		income: numberOr(annual?.income),
		daysLate: numberOr(annual?.daysLate),
		status,
		obligated: hasAnnualObligation(annual, monthlyRows),
		open: !CLOSED_ANNUAL_STATUSES.includes(status),
		sellable: SELLABLE_STATUSES.includes(status) && !purchased && !annual?.isFuture,
		purchased,
		settled: purchased,
		inProgress: IN_PROGRESS_STATUSES.includes(status),
	};
};

export type LegacySummaryInput = {
	monthlyRows: readonly any[] | null | undefined;
	annualRows: readonly any[] | null | undefined;
	loading?: boolean;
};

export const summaryFromLegacy = ({ monthlyRows, annualRows, loading = false }: LegacySummaryInput): DeclarationsSummary => {
	const rows = monthlyRows ?? [];
	const months: SummaryMonth[] = rows.map((row) => ({
		year: row?.year,
		month: row?.month,
		regimes: Array.isArray(row?.regimes) ? row.regimes : [],
		cells: (row?.declarations ?? []).map(cellOf),
	}));
	return {
		source: 'legacy',
		loading: loading || !monthlyRows || !annualRows,
		reliable: true,
		months,
		annuals: (annualRows ?? []).map((annual) => annualOf(annual, rows)),
	};
};
